import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

export default function CoachAssistantBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    try {
      const r = await api.get("/coach/assistant/history");
      setMessages(r.data.messages || []);
    } catch { /* silencieux */ }
  };

  useEffect(() => {
    if (open && !loaded) { loadHistory(); setLoaded(true); }
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/assistant/message`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ content: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch { toast.error("Erreur de connexion"); }
    finally { setStreaming(false); }
  };

  const reset = async () => {
    try { const r = await api.post("/coach/assistant/reset"); setMessages(r.data.messages || []); }
    catch { toast.error("Impossible de réinitialiser"); }
  };

  const suggestions = [
    "Quel client est en surcharge cette semaine ?",
    "Qui est à surveiller en priorité ?",
    "Propose un ajustement pour un client fatigué",
  ];

  return (
    <>
      {/* Bulle flottante */}
      <button
        data-testid="coach-assistant-bubble"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-sage text-obsidian shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        aria-label="Assistant IA du coach"
      >
        {open ? <X size={22} /> : <Bot size={24} strokeWidth={1.8} />}
      </button>

      {/* Panneau de chat */}
      {open && (
        <div
          data-testid="coach-assistant-panel"
          className="fixed bottom-24 right-5 z-50 w-[min(400px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-8rem))] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-obsidian text-white shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center"><Sparkles size={15} className="text-sage" /></span>
              <div>
                <div className="text-sm font-medium leading-none">Assistant IA du coach</div>
                <div className="text-[11px] text-white/60 mt-0.5">Privé · données clients en temps réel</div>
              </div>
            </div>
            <button data-testid="coach-assistant-reset" onClick={reset} title="Réinitialiser" className="p-1.5 rounded-full hover:bg-white/10 text-white/70"><RotateCcw size={15} /></button>
          </div>

          <div ref={scrollRef} data-testid="coach-assistant-messages" className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 bg-secondary/30">
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-obsidian text-white px-3.5 py-2.5 text-sm whitespace-pre-wrap">{m.content}</div>
                </div>
              ) : (
                <div key={i} className="flex gap-2.5">
                  <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><Sparkles size={13} className="text-sage" /></span>
                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-border px-3.5 py-2.5 text-sm text-obsidian/85 whitespace-pre-wrap">
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin text-obsidian/40" /> : "")}
                  </div>
                </div>
              )
            ))}

            {messages.length <= 1 && (
              <div className="pt-1 space-y-2">
                {suggestions.map((s, i) => (
                  <button key={i} data-testid={`coach-assistant-suggestion-${i}`} onClick={() => setInput(s)}
                    className="block w-full text-left text-xs text-obsidian/70 bg-white border border-border rounded-xl px-3 py-2 hover:border-sage transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-border shrink-0 flex items-end gap-2 bg-white">
            <textarea
              data-testid="coach-assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Pose ta question sur un client…"
              rows={1}
              className="flex-1 resize-none max-h-24 rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-sage"
            />
            <button data-testid="coach-assistant-send" onClick={send} disabled={streaming || !input.trim()}
              className="w-10 h-10 shrink-0 rounded-xl bg-sage text-obsidian flex items-center justify-center disabled:opacity-40 hover:bg-sage/90 transition-colors">
              {streaming ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
